import { Injectable, OnDestroy, OnInit } from '@angular/core';
import NoSleep from '@zakj/no-sleep';
import * as moment from 'moment';
import { UserService } from './user.service';
import { IdleService } from './idle.service';
import { Subscription, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ScreenWakeService {

  private wakeLock: any;
  private noSleep: any;
  private wakeLockActivated: boolean = false;

  private videoElement: HTMLVideoElement | undefined = undefined;

  private wakeLockInterval: any = undefined;

  private clickListener?: EventListenerOrEventListenerObject;
  private touchstartListener?: EventListenerOrEventListenerObject;

  private logoutInterval?: number;

  private subscriptions: Subscription[] = [];

  constructor(
    private userService: UserService,
    private idle: IdleService
  ) {
    // Server-side render has no document; the listener only matters in the browser.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }

    this.subscriptions.push(this.userService.userProfile$
      .subscribe(user => {
        this.logoutInterval = user?.autoLogoutTime ?? 60;
      }));

  }

  private shouldSkipScreenWake() {

    const skipRoutes = [
      'verify-email',
      'verify-prescription',
      'verify-report',
      'reset-password',
      'accept-invite',
      'reject-invite',
      'patient-invite',
      'patient-satisfaction',
      'login'
    ];

    return skipRoutes.some(route => document.URL.includes(route));
  }

  private handleVisibilityChange() {
    if (this.shouldSkipScreenWake()) {
      return;
    }
    console.log(`App visibility state is ${document.visibilityState}`);
    if (document.visibilityState === 'visible') {
      const storedLastActive = localStorage.getItem('lastActive');
      if (storedLastActive) {
        const data = JSON.parse(storedLastActive);
        const lastActive = moment(data.timestamp);
        const currentTime = moment().valueOf();
        const maxInactiveTime = moment(lastActive).add(180, 'minutes').valueOf();
        if (currentTime >= maxInactiveTime) {
          console.log(`Maximum visibility window reached, triggering logout`, currentTime, maxInactiveTime);
          this.logout();
          this.resetState();
          return;
        }
        else {
          localStorage.removeItem('lastActive');
          this.validateAuthToken();
        }
      } else {
        this.validateAuthToken();
      }
    } else {
      localStorage.setItem('lastActive', JSON.stringify({ timestamp: moment().valueOf(), interval: this.logoutInterval }))
    }
  }

  private validateAuthToken() {
    const isValid = this.userService.validateToken();
    if (!isValid || this.idle.isShowingLogout) {
      console.log(`Session has expired, triggering logout`);
      this.resetState();
      this.logout();
    } else if (this.wakeLockActivated) {
      this.enableWakeLock();
    }
  }

  private logout() {
    // One navigation to a fresh /login — see `UserService.logoutAndRedirect`.
    this.userService.logoutAndRedirect();
  }

  // public method used to disable screen sleeping
  async disableScreenSleep(): Promise<void> {
    console.log('disableScreenSleep was called and navigator has wakeLock: ', ('wakeLock' in navigator));
    this.enableWakeLock();
  }

  //public method used to enable screen sleeping
  enableScreenSleep(): void {
    console.log('enableScreenSleep was called. Resetting wakeLock state...');
    this.resetState();
  }

  // binds to user actions before enabling wakeLock features
  private bindActionListener() {
    try {
      console.log('setupActionListener called to wait for user interaction before initializing wakeLock');
      this.clickListener = this.setupNoSleep.bind(this);
      this.touchstartListener = this.setupNoSleep.bind(this);

      document.addEventListener('click', this.clickListener, false);
      document.addEventListener('touchstart', this.touchstartListener, false);
    } catch (error) {
      console.error('bindActionListener failed', error);
    }
  }

  // enable all the approaches to attempt to keep screen active in all scenarios
  private async enableWakeLock() {
    await this.requestWakeLock();
    //await this.createAndPlayVideo();
    //this.releaseActionListener();
  }

  // check if wakelock is available in the navigator else fallback to noSleep.js
  private async requestWakeLock() {
    try {
      if ('wakeLock' in navigator) {
        try {
          // checks if wakeLock is currently active and releases it if needed before creating a new wakeLock
          this.releaseWakeLock();

          // creates a new wakeLock instance and sets it to be recreated at the specified interval to ensure iOS doesnt destory it after 1 minute
          this.wakeLock = await (navigator as any).wakeLock.request('screen');
          console.log('wakeLock is active');
          this.wakeLockActivated = true;
          //this.startWakeLockInterval();
        } catch (err) {
          console.error('Failed to request wake lock:', err);
          // fallback to noSleep.js if something goes wrong when attempting to initialize wakeLock
          this.bindActionListener();
        }
      } else {
        // fallback to noSleep.js if if wakeLock is not available on the current device
        this.bindActionListener();
      }
    } catch (error) {
      console.log('A call to requestWakeLock failed', error);
      this.bindActionListener();
    }
  }

  // create a wakeLock reset interval 
  private startWakeLockInterval() {
    try {
      this.clearWakeLockInterval();
      this.createWakeLockIntervalChecking();
    } catch (error) {
      console.error('failed to start wakeLock interval checking', error);
    }
  }

  // create an interval to recreate the wakeLock every 30 seconds
  private createWakeLockIntervalChecking() {
    console.log('create the wakeLock interval check');
    this.clearWakeLockInterval();
    this.wakeLockInterval = setInterval(async () => {
      console.log('wakeLock interval reached, requesting new wakeLock');
      await this.requestWakeLock();
    }, 30000); // Re-request wake lock every 30 seconds
  }

  // create a video element in the dom that plays in a loop to attempt to keep screen active
  /* private async createAndPlayVideo(): Promise<void> {
    try {
      if (this.videoElement) return;

      this.videoElement = document.createElement('video');
      this.videoElement.src = '/assets/videos/silent-1s.mp4'; // Path to your silent video file
      this.videoElement.loop = true;
      this.videoElement.muted = true;
      this.videoElement.playsInline = true; // Prevents fullscreen video popup on iOS devices

      // Style the video element to be full screen behind any open dialogs
      this.videoElement.style.position = 'fixed';
      this.videoElement.style.width = '100vw';
      this.videoElement.style.height = '100vh';
      this.videoElement.style.top = '0px';
      this.videoElement.style.left = '0px';
      this.videoElement.style.pointerEvents = 'none';
      this.videoElement.style.zIndex = '-1';

      document.body.appendChild(this.videoElement);
      console.log('Video element has been created to keep the screen awake');
      // Listen for the 'canplay' event before attempting to play the video
      this.videoElement.addEventListener('canplay', () => {
        this.videoElement?.play().catch((error) => {
          console.error('Failed to play the video:', error);
        });
      });

      // Listen for the 'error' event to catch and log errors related to video loading
      this.videoElement.addEventListener('error', (error) => {
        console.error('Failed to load the video:', error);
      });
    } catch (error) {
      console.error('failed to initialize background playback video', error);
    }
  } */



  // initialize noSleep.js as a fallback to wakeLock in case of wakeLock not available or failure
  private setupNoSleep() {
    this.releaseActionListener();
    console.log('setup noSleep as an alternative to wakeLock');
    try {
      if (this.noSleep) {
        console.log('noSleep is already active. Attempting to recreate it...');
        this.noSleep.disable();
        this.noSleep = undefined;
      }
      this.noSleep = new NoSleep();
      this.noSleep.enable();
      console.log('noSleep is active');

    } catch (error) {
      console.error('failed to initialize noSleep', error);
    }
  }

  // completely reset the state of this service to ensure proper cleanup
  private resetState() {
    try {
      this.releaseActionListener();
      //this.clearWakeLockInterval();
      this.releaseWakeLock();
      //this.removeVideo();
      this.disableNoSleep();

      document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
      this.subscriptions.forEach(sub => sub.unsubscribe());

    } catch (error) {
      console.error('Failed to reset the screen-wake state', error);
    }
  }

  // unbind the user action listeners
  private releaseActionListener() {
    try {
      if (this.clickListener) {
        document.removeEventListener('click', this.clickListener, false);
      }
      if (this.touchstartListener) {
        document.removeEventListener('touchstart', this.touchstartListener, false);
      }
    } catch (error) {
      console.error('failed to release the user action listeners', error);
    }
  }

  // clear wakeLock interval checking
  private clearWakeLockInterval() {
    if (this.wakeLockInterval) {
      console.log('clearing the wakeLock interval check');
      clearInterval(this.wakeLockInterval);
      this.wakeLockInterval = undefined;
    }
  }

  // release the current wakeLock
  private releaseWakeLock() {
    if (this.wakeLock) {
      this.wakeLock.release();
      this.wakeLock = undefined;
      this.wakeLockActivated = false;
      console.log('wakeLock was released');
    }
  }

  // remove the video element if it is available
  private removeVideo() {
    try {
      if (this.videoElement) {
        this.videoElement.pause();
        this.videoElement.remove();
        this.videoElement = undefined;
        console.log('Video element removed and paused');
      }
    } catch (error) {
      console.error('failed to remove the background video element');
    }
  }

  // disable noSleep.js if it is initialized
  private disableNoSleep() {
    try {
      if (this.noSleep) {
        this.noSleep.disable();
        this.noSleep = undefined;
      }
    } catch (error) {
      console.error('failed to disable noSleep', error);
    }
  }
}
